import { useEffect, useState } from 'preact/hooks';
import { api, mensagemDoErro, qs } from '../api';
import { CampoSenha } from '../componentes/Campos';
import { ROTULO_PERFIL, plural } from '../format';
import { useApi, useDebounce } from '../hooks';
import { useMunicipios, useSecretarias } from '../lookups';
import { pode } from '../permissoes';
import { inspecionar } from '../session';
import { usuario } from '../state';
import type { Perfil, SessaoAtiva, UsuarioAdmin } from '../types';
import { Campo, Carregando, ErroForm, Modal, Selo, avisar, confirmar } from '../ui';
import { dataHora, useRecarga } from './comum';
import { SeletorLista } from './Seletores';

const PERFIS: Perfil[] = ['SUPERADMIN', 'ADMIN_GESTOR', 'SECRETARIO', 'OPERADOR', 'PESQUISADOR', 'LEITOR'];

const AJUDA_PERFIL: Record<Perfil, string> = {
  SUPERADMIN: 'Acesso total, inclusive a contas de outros superadmins.',
  ADMIN_GESTOR: 'Vê tudo e gerencia usuários, secretarias, projetos, ações e pesquisas.',
  SECRETARIO: 'Gerencia projetos e ações apenas das secretarias atribuídas. Não cria usuários.',
  OPERADOR: 'Cadastra e atualiza ações das secretarias atribuídas. Não altera projetos.',
  PESQUISADOR: 'Lança respostas de pesquisa nos municípios atribuídos. Não vê o painel nem os resultados.',
  LEITOR: 'Só consulta: painel, ações, projetos e pesquisas, sem alterar nada.',
};

const precisaSecretaria = (p: Perfil) => p === 'SECRETARIO' || p === 'OPERADOR';

export function UsuariosPage() {
  const eu = usuario.value!;
  const podeEscrever = pode(eu.perfil, 'usuario:write');
  const { lista: secretarias, porId } = useSecretarias();
  const [busca, setBusca] = useState('');
  const [perfil, setPerfil] = useState('');
  const [ativo, setAtivo] = useState('');
  const q = useDebounce(busca, 300);
  const [recarga, recarregar] = useRecarga();
  const lista = useApi<UsuarioAdmin[]>(`/usuarios${qs({ q, perfil, ativo })}`, recarga);
  const [editando, setEditando] = useState<UsuarioAdmin | 'novo' | null>(null);
  const [senhaGerada, setSenhaGerada] = useState<{ nome: string; email: string; senha: string } | null>(null);

  const usuarios = lista.dados ?? [];

  async function abrirInspecao(u: UsuarioAdmin) {
    const ok = await confirmar(
      `Inspecionar a conta de ${u.nome}? Você vai ver o sistema exatamente como essa pessoa vê (${ROTULO_PERFIL[u.perfil]}), só para leitura: nada poderá ser alterado. Dura até 30 minutos e fica registrado na auditoria.`,
      { confirmar: 'Inspecionar' },
    );
    if (!ok) return;
    try {
      await inspecionar(u.id);
    } catch (e) {
      avisar(mensagemDoErro(e), 'erro');
    }
  }

  return (
    <main class="pagina">
      <header class="pagina-topo">
        <div>
          <h1>Usuários e acessos</h1>
          <p class="dica">Só o gestor cria contas. Cada pessoa recebe uma senha temporária e troca no primeiro acesso.</p>
        </div>
        {podeEscrever && <button type="button" class="botao primario" onClick={() => setEditando('novo')}>+ Novo usuário</button>}
      </header>

      <section class="barra-filtros" aria-label="Filtros">
        <label class="campo"><span>Buscar</span><input type="search" value={busca} onInput={(e) => setBusca(e.currentTarget.value)} placeholder="Nome ou e-mail…" /></label>
        <label class="campo"><span>Perfil</span>
          <select value={perfil} onChange={(e) => setPerfil(e.currentTarget.value)}>
            <option value="">Todos</option>
            {PERFIS.map((p) => <option key={p} value={p}>{ROTULO_PERFIL[p]}</option>)}
          </select>
        </label>
        <label class="campo"><span>Situação</span>
          <select value={ativo} onChange={(e) => setAtivo(e.currentTarget.value)}>
            <option value="">Todos</option><option value="true">Ativos</option><option value="false">Desativados</option>
          </select>
        </label>
      </section>

      {lista.erro && <ErroForm texto={mensagemDoErro(lista.erro)} />}
      {lista.carregando && !lista.dados && <Carregando />}

      <div class="tabela-rolagem">
        <table class="tabela">
          <caption class="so-leitor">Lista de usuários</caption>
          <thead><tr><th scope="col">Pessoa</th><th scope="col">Perfil</th><th scope="col">Escopo</th><th scope="col">Situação</th><th scope="col">Último acesso</th><th scope="col"><span class="so-leitor">Ações</span></th></tr></thead>
          <tbody>
            {usuarios.map((u) => {
              const souEu = u.id === eu.id;
              // O gestor não mexe em contas de superadmin (a API também recusa).
              const bloqueado = u.perfil === 'SUPERADMIN' && eu.perfil !== 'SUPERADMIN';
              return (
                <tr key={u.id} class={u.ativo ? '' : 'cancelada'}>
                  <td><strong>{u.nome}</strong>{souEu && <small> (você)</small>}<small class="sub">{u.email}</small></td>
                  <td>{ROTULO_PERFIL[u.perfil]}</td>
                  <td>
                    {precisaSecretaria(u.perfil)
                      ? u.secretariaIds.map((id) => porId.get(id)?.sigla ?? '?').join(', ') || '—'
                      : u.perfil === 'PESQUISADOR' ? plural(u.codIbges.length, 'município', 'municípios') : 'Todo o estado'}
                  </td>
                  <td class="chips">
                    <Selo texto={u.ativo ? 'Ativo' : 'Desativado'} tom={u.ativo ? 'verde' : 'cinza'} />
                    {u.ativo && u.deveTrocarSenha && <Selo texto="Senha temporária" tom="ambar" />}
                    {!u.temSenha && <Selo texto="Só Google" tom="azul" />}
                  </td>
                  <td>{dataHora(u.ultimoLoginEm)}</td>
                  <td class="acoes-linha">
                    {podeEscrever && !bloqueado && !souEu && u.ativo && (
                      <button type="button" class="link" onClick={() => void abrirInspecao(u)} title="Ver o sistema como esta pessoa vê (somente leitura)">Inspecionar</button>
                    )}
                    {podeEscrever && !bloqueado && <button type="button" class="link" onClick={() => setEditando(u)}>Editar</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!lista.carregando && usuarios.length === 0 && !lista.erro && <div class="vazio-grande"><p>Nenhum usuário encontrado.</p></div>}
      </div>

      {editando && (
        <UsuarioForm
          usuarioEdit={editando === 'novo' ? null : editando}
          secretarias={secretarias.filter((s) => s.ativa)}
          onFechar={() => setEditando(null)}
          onSalvo={(senha) => {
            const alvo = editando;
            setEditando(null);
            recarregar();
            if (senha && alvo === 'novo') setSenhaGerada(senha);
          }}
          onSenhaResetada={(s) => { setEditando(null); recarregar(); setSenhaGerada(s); }}
        />
      )}
      {senhaGerada && <SenhaTemporaria dados={senhaGerada} onFechar={() => setSenhaGerada(null)} />}
    </main>
  );
}

type Senha = { nome: string; email: string; senha: string };

function UsuarioForm({ usuarioEdit, secretarias, onFechar, onSalvo, onSenhaResetada }: {
  usuarioEdit: UsuarioAdmin | null;
  secretarias: { id: string; sigla: string; nome: string }[];
  onFechar: () => void;
  onSalvo: (senha?: Senha) => void;
  onSenhaResetada: (s: Senha) => void;
}) {
  const eu = usuario.value!;
  const { lista: municipios } = useMunicipios();
  const editando = usuarioEdit !== null;
  const souEu = usuarioEdit?.id === eu.id;

  const [nome, setNome] = useState(usuarioEdit?.nome ?? '');
  const [email, setEmail] = useState(usuarioEdit?.email ?? '');
  const [perfil, setPerfil] = useState<Perfil>(usuarioEdit?.perfil ?? 'OPERADOR');
  const [ativo, setAtivo] = useState(usuarioEdit?.ativo ?? true);
  const [secIds, setSecIds] = useState<string[]>(usuarioEdit?.secretariaIds ?? []);
  const [codIbges, setCodIbges] = useState<string[]>(usuarioEdit?.codIbges ?? []);
  const [somenteGoogle, setSomenteGoogle] = useState(false);
  const [erro, setErro] = useState('');
  const [erroSec, setErroSec] = useState('');
  const [salvando, setSalvando] = useState(false);

  const sessoes = useApi<SessaoAtiva[]>(usuarioEdit ? `/usuarios/${usuarioEdit.id}/sessoes` : null);

  // Perfil mudou: limpa o escopo que não se aplica mais, para não gravar lixo.
  useEffect(() => {
    if (!precisaSecretaria(perfil)) setSecIds([]);
    if (perfil !== 'PESQUISADOR') setCodIbges([]);
  }, [perfil]);

  const perfisPermitidos = PERFIS.filter((p) => p !== 'SUPERADMIN' || eu.perfil === 'SUPERADMIN');

  async function salvar(e: Event) {
    e.preventDefault();
    setErro('');
    setErroSec('');
    if (precisaSecretaria(perfil) && secIds.length === 0) return setErroSec('Selecione ao menos uma secretaria para este perfil.');
    setSalvando(true);
    try {
      if (usuarioEdit) {
        await api(`/usuarios/${usuarioEdit.id}`, { method: 'PATCH', body: { nome: nome.trim(), perfil, ativo, secretariaIds: secIds, codIbges } });
        avisar('Usuário atualizado.');
        onSalvo();
      } else {
        const r = await api<{ usuario: UsuarioAdmin; senhaTemporaria: string | null }>('/usuarios', {
          method: 'POST',
          body: { nome: nome.trim(), email: email.trim(), perfil, secretariaIds: secIds, codIbges, somenteGoogle },
        });
        avisar('Usuário criado.');
        onSalvo(r.senhaTemporaria ? { nome: r.usuario.nome, email: r.usuario.email, senha: r.senhaTemporaria } : undefined);
      }
    } catch (err) {
      setErro(mensagemDoErro(err));
    } finally {
      setSalvando(false);
    }
  }

  async function resetar() {
    if (!usuarioEdit) return;
    if (!(await confirmar(`Gerar uma nova senha temporária para ${usuarioEdit.nome}? A senha atual deixa de valer e todas as sessões dela serão encerradas.`, { confirmar: 'Gerar nova senha', perigo: true }))) return;
    try {
      const r = await api<{ senhaTemporaria: string }>(`/usuarios/${usuarioEdit.id}/resetar-senha`, { method: 'POST' });
      onSenhaResetada({ nome: usuarioEdit.nome, email: usuarioEdit.email, senha: r.senhaTemporaria });
    } catch (err) {
      setErro(mensagemDoErro(err));
    }
  }

  async function encerrarSessoes() {
    if (!usuarioEdit) return;
    if (!(await confirmar(`Encerrar todas as sessões de ${usuarioEdit.nome}? Ela precisará entrar de novo.`, { confirmar: 'Encerrar sessões' }))) return;
    try {
      await api(`/usuarios/${usuarioEdit.id}/sessoes`, { method: 'DELETE' });
      avisar('Sessões encerradas.');
      onSalvo();
    } catch (err) {
      setErro(mensagemDoErro(err));
    }
  }

  return (
    <Modal titulo={editando ? `Editar ${usuarioEdit.nome}` : 'Novo usuário'} onFechar={onFechar} largo>
      <form class="form" onSubmit={salvar}>
        <div class="linha-form">
          <Campo rotulo="Nome *" larg="meio">{(id) => <input id={id} required minLength={2} maxLength={120} value={nome} onInput={(e) => setNome(e.currentTarget.value)} />}</Campo>
          <Campo rotulo="E-mail *" larg="meio" dica={editando ? 'O e-mail não pode ser alterado.' : 'É com ele que a pessoa entra (inclusive pelo Google).'}>
            {(id) => <input id={id} type="email" required disabled={editando} value={email} onInput={(e) => setEmail(e.currentTarget.value)} autocomplete="off" />}
          </Campo>
        </div>

        <Campo rotulo="Perfil *" dica={AJUDA_PERFIL[perfil]}>
          {(id) => (
            <select id={id} value={perfil} disabled={souEu} onChange={(e) => setPerfil(e.currentTarget.value as Perfil)}>
              {perfisPermitidos.map((p) => <option key={p} value={p}>{ROTULO_PERFIL[p]}</option>)}
            </select>
          )}
        </Campo>
        {souEu && <p class="dica">Você não pode alterar o próprio perfil nem se desativar.</p>}

        {precisaSecretaria(perfil) && (
          <SeletorLista titulo="Secretarias com acesso *" itens={secretarias.map((s) => ({ id: s.id, rotulo: s.sigla, sub: s.nome }))} marcados={secIds} onMudar={setSecIds} erro={erroSec} />
        )}
        {perfil === 'PESQUISADOR' && (
          <SeletorLista titulo="Municípios onde pode coletar" itens={municipios.map((m) => ({ id: m.cod, rotulo: m.nome, sub: m.mesorregiao.replace(' Paraibano', '').replace(' Paraibana', '') }))} marcados={codIbges} onMudar={setCodIbges} buscavel alturaMax={220} />
        )}
        {precisaSecretaria(perfil) && secretarias.length === 0 && <p class="aviso">Não há secretaria ativa. Cadastre uma na tela Secretarias.</p>}

        {!editando && (
          <label class="check">
            <input type="checkbox" checked={somenteGoogle} onChange={(e) => setSomenteGoogle(e.currentTarget.checked)} /> Esta pessoa entra só pelo Google (sem senha)
          </label>
        )}
        {editando && !souEu && (
          <label class="check"><input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.currentTarget.checked)} /> Conta ativa <small>(desativar derruba o acesso na hora)</small></label>
        )}

        {editando && (
          <section class="bloco">
            <h3>Acesso</h3>
            <p class="dica">
              {sessoes.carregando && !sessoes.dados ? 'Verificando sessões…' : `${plural(sessoes.dados?.length ?? 0, 'sessão ativa', 'sessões ativas')}.`}{' '}
              {usuarioEdit.googleVinculado ? 'Google vinculado.' : ''}
            </p>
            <div class="linha-form">
              <button type="button" class="botao" onClick={() => void resetar()}>Gerar nova senha temporária</button>
              {!souEu && <button type="button" class="botao" onClick={() => void encerrarSessoes()} disabled={(sessoes.dados?.length ?? 0) === 0}>Encerrar todas as sessões</button>}
            </div>
          </section>
        )}

        <ErroForm texto={erro} />
        <div class="modal-acoes">
          <button type="button" class="botao" onClick={onFechar}>Cancelar</button>
          <button type="submit" class="botao primario" disabled={salvando}>{salvando ? 'Salvando…' : editando ? 'Salvar alterações' : 'Criar usuário'}</button>
        </div>
      </form>
    </Modal>
  );
}

function SenhaTemporaria({ dados, onFechar }: { dados: Senha; onFechar: () => void }) {
  const [copiado, setCopiado] = useState(false);
  async function copiar() {
    try {
      await navigator.clipboard.writeText(dados.senha);
      setCopiado(true);
    } catch {
      avisar('Não foi possível copiar. Selecione a senha e copie manualmente.', 'erro');
    }
  }
  return (
    <Modal titulo="Senha temporária" onFechar={onFechar} pequeno>
      <p>Entregue esta senha a <strong>{dados.nome}</strong> ({dados.email}) por um canal seguro.</p>
      <div class="senha-caixa">
        {/* Oculta por padrão (tem gente olhando a tela): o olhinho mostra; "Copiar" funciona com ela oculta. */}
        <label class="so-leitor" for="senha-temp">Senha temporária de {dados.nome}</label>
        <CampoSenha id="senha-temp" value={dados.senha} testid="senha-temporaria" />
        <button type="button" class="botao" onClick={() => void copiar()}>{copiado ? 'Copiada ✓' : 'Copiar'}</button>
      </div>
      <p class="aviso">Ela só aparece agora. Se for perdida, gere outra. No primeiro acesso a pessoa será obrigada a trocá-la.</p>
      <div class="modal-acoes"><button type="button" class="botao primario" onClick={onFechar}>Entendi, já anotei</button></div>
    </Modal>
  );
}
